import { useEffect, useState, type FormEvent } from 'react';
import {
  signIn,
  fetchUserAttributes,
  fetchAuthSession,
  type SignInOutput,
} from 'aws-amplify/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Auth.css';
import { useUserContext } from '../Context/UserContext';

interface SignInFormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}
interface SignInForm extends HTMLFormElement {
  readonly elements: SignInFormElements;
}

const DEFAULT_NEXT = '/';

function sanitizeNext(next?: string | null): string {
  const fallback = DEFAULT_NEXT;
  if (!next) return fallback;
  if (!next.startsWith('/')) return fallback;

  const lower = next.toLowerCase();

  if (
    lower.startsWith('/login') ||
    lower.startsWith('/register') ||
    lower.startsWith('/forgot-password')
  ) {
    return fallback;
  }

  if (
    lower.startsWith('/assets') ||
    lower.startsWith('/static') ||
    lower.startsWith('/build') ||
    lower.startsWith('/vite') ||
    lower.endsWith('.js') ||
    lower.endsWith('.css') ||
    lower.endsWith('.map')
  ) {
    return fallback;
  }

  return next;
}

function resolveNext(location: ReturnType<typeof useLocation>): string {
  const qp = new URLSearchParams(location.search);

  const nextFromQueryRaw = qp.get('next') || '';
  const nextFromQuery = (() => {
    if (!nextFromQueryRaw) return '';
    try {
      return decodeURIComponent(nextFromQueryRaw);
    } catch {
      return nextFromQueryRaw;
    }
  })();

  const nextFromState = (location.state as any)?.next || '';

  // Privacy-first: sessionStorage only
  let nextFromStorage = '';
  try {
    nextFromStorage = sessionStorage.getItem('nextAfterAuth') || '';
  } catch {}

  const roleRaw = qp.get('role') || qp.get('r') || '';
  const idRaw = qp.get('id') || qp.get('sessionId') || qp.get('sid') || '';
  let derived = '';

  if (idRaw && roleRaw) {
    const role = roleRaw.trim().toLowerCase();
    const id = encodeURIComponent(idRaw.trim());
    if (role.startsWith('coll')) derived = `/coll/${id}`;
    else if (role.startsWith('init')) derived = `/session/${id}`;
  }

  const preferredRaw = nextFromQuery || nextFromState || derived || nextFromStorage;
  const chosen = sanitizeNext(preferredRaw) || DEFAULT_NEXT;

  return chosen;
}

export default function LoginComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useUserContext();

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = resolveNext(location);

  const qp = new URLSearchParams(location.search);
  const nextFromQueryRaw = qp.get('next') || '';

  const registerHref =
    nextFromQueryRaw
      ? `/register?next=${nextFromQueryRaw}`
      : next && next !== DEFAULT_NEXT
        ? `/register?next=${encodeURIComponent(next)}`
        : '/register';

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAuthSession();
        if (s?.tokens?.idToken || s?.tokens?.accessToken) {
          try {
            const attrs = await fetchUserAttributes().catch(() => ({} as any));
            const em = (attrs as any)?.email || (attrs as any)?.preferred_username || '';
            if (em && em.includes('@')) sessionStorage.setItem('email', em);
          } catch {}
          await refreshUser();
          navigate(next, { replace: true });
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function friendlyError(err: any): string {
    const name = err?.name || '';
    const msg = err?.message || '';
    switch (name) {
      case 'UserNotFoundException':
        return 'No account exists with that email.';
      case 'NotAuthorizedException':
        return 'Incorrect email or password.';
      case 'UserNotConfirmedException':
        return 'Please confirm your email before signing in.';
      case 'PasswordResetRequiredException':
        return 'A password reset is required. Use “Forgot Password”.';
      default:
        return msg || 'Sign in failed. Please try again.';
    }
  }

  async function handleSubmit(event: FormEvent<SignInForm>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = event.currentTarget;
    const email = form.elements.email.value.trim();
    const password = form.elements.password.value;

    try {
      const result: SignInOutput = await signIn({ username: email, password });

      if (!result.isSignedIn) {
        const step = result.nextStep?.signInStep;

        if (step === 'CONFIRM_SIGN_UP') {
          setError('Please confirm your email. We can resend the code from the Register page.');
        } else if (
          step === 'RESET_PASSWORD' ||
          step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          setError('A new password is required. Use “Forgot Password”.');
        } else if (
          step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
          step === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
          step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE' ||
          step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' ||
          step === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
        ) {
          setError('Additional verification is required to complete sign-in.');
        } else {
          setError('Sign-in did not complete. Please try again.');
        }
        return;
      }

      const s = await fetchAuthSession();

      if (!s?.tokens?.idToken && !s?.tokens?.accessToken) {
        setError('Sign-in did not complete. Please try again.');
        return;
      }

      try {
        const attrs = await fetchUserAttributes().catch(() => ({} as any));
        const em = (attrs as any)?.email || (attrs as any)?.preferred_username || '';
        if (em && em.includes('@')) sessionStorage.setItem('email', em);
      } catch {}

      // Clear any legacy navigation hints (both stores to be safe).
      try {
        sessionStorage.removeItem('nextAfterAuth');
        sessionStorage.removeItem('pendingSessionId');
        sessionStorage.removeItem('registerSource');
        sessionStorage.removeItem('email');

        localStorage.removeItem('nextAfterAuth');
        localStorage.removeItem('pendingSessionId');
        localStorage.removeItem('registerSource');
        localStorage.removeItem('email');

        localStorage.removeItem('lastSessionId');
      } catch {}

      await refreshUser();
      navigate(next, { replace: true });
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-container">
      <h2>Sign In</h2>
      <form onSubmit={handleSubmit} autoComplete="on">
        <input
          name="email"
          type="email"
          placeholder="Email"
          autoComplete="username"
          required
        />
        <input
          name="password"
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          required
        />
        <button type="submit" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>
        {error && <div className="error">{error}</div>}
      </form>
      <div className="auth-footer">
        <Link to={registerHref}>Register</Link> |{' '}
        <Link to="/forgot-password">Forgot Password?</Link>
      </div>
    </div>
  );
}
